import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Typography,
  MenuItem,
  Container,
  CircularProgress,
  Tabs,
  Tab,
  Box,
  Card,
  CardContent,
  Fade,
  Chip,
  alpha,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useForm, Controller } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import CalculateIcon from "@mui/icons-material/Calculate";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { apiClient } from "../../api/httpClient";
import { calculatePremium } from "../../globals/utils/commons";
import { formatCurrency, getBondFromGross } from "./premiumCalculatorUtils";

const categories = [
  { category: "Probate" },
  {
    category: "Guardianship",
    sub_categories: [
      "Guardian - Incapacitated",
      "Guardian - Minor",
      "Guardian - Advocate",
      "Conservator",
    ],
  },
  { category: "Trustee" },
  { category: "VA" },
  { category: "Blanket" },
];

const NumberFormatCustom = React.forwardRef(function NumberFormatCustom(props, ref) {
  const { onChange, ...other } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      thousandSeparator
      isNumericString
      prefix="$"
      decimalScale={0}
    />
  );
});

export { formatCurrency, getBondFromGross } from "./premiumCalculatorUtils";

const THEMES = {
  authenticated: {
    accent: "#2563eb",
    accentRgba: (opacity) => `rgba(37, 99, 235, ${opacity})`,
    cardBg: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 50%, #f8f9fa 100%)",
    cardBorder: "1px solid rgba(37, 99, 235, 0.2)",
    titleGradient: "linear-gradient(135deg, #2563eb 0%, #1e3a8a 50%, #111827 100%)",
    buttonBg: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
    buttonHover: "linear-gradient(135deg, #1d4ed8 0%, #1e3a8a 100%)",
    headingColor: "#000000",
    resultCardBg: null,
    resultBorderColor: null,
    resultValueColor: null,
    resultPremiumColor: null,
    chipVariant: "outlined",
    homesteadChipColor: "info",
    containerMt: 4,
    cardHoverShadow: "0 4px 16px rgba(37, 99, 235, 0.15)",
    buttonShadow: (opacity) => `0 4px 20px rgba(37, 99, 235, ${opacity})`,
    buttonHoverShadow: (opacity) => `0 6px 25px rgba(37, 99, 235, ${opacity})`,
  },
  public: {
    accent: "#000000",
    accentRgba: (opacity) => `rgba(0, 0, 0, ${opacity})`,
    cardBg: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
    cardBorder: "1px solid",
    cardBorderColor: alpha("#000000", 0.1),
    titleGradient: "linear-gradient(45deg, #000000, #333333)",
    buttonBg: "linear-gradient(45deg, #000000, #333333)",
    buttonHover: "linear-gradient(45deg, #333333, #666666)",
    headingColor: "transparent",
    resultCardBg: "linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)",
    resultBorderColor: alpha("#000000", 0.2),
    resultValueColor: "#333333",
    resultPremiumColor: "#000000",
    chipVariant: "outlined",
    homesteadChipColor: "info",
    containerMt: 0,
    cardHoverShadow: null,
    buttonShadow: () => "0 4px 20px rgba(0, 0, 0, 0.3)",
    buttonHoverShadow: () => "0 6px 25px rgba(0, 0, 0, 0.4)",
  },
};

function buildInputSx(theme) {
  return {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      backgroundColor: alpha(theme.accent, 0.02),
      "& fieldset": { borderColor: theme.accentRgba(0.2) },
      "&:hover fieldset": { borderColor: theme.accentRgba(0.4) },
      "&.Mui-focused fieldset": {
        borderColor: theme.accent,
        borderWidth: 2,
      },
    },
  };
}

function parseCurrencyInput(value) {
  return Number.parseInt(String(value).replace(/\D/g, ""), 10) || 0;
}

function buildTitleSx(variant, theme) {
  if (variant === "public") {
    return {
      fontWeight: 700,
      background: theme.titleGradient,
      backgroundClip: "text",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      mb: 1,
    };
  }
  return { fontWeight: 700, color: theme.headingColor, mb: 1 };
}

function buildCardSx(theme) {
  return {
    borderRadius: 4,
    background: theme.cardBg,
    border: theme.cardBorder,
    ...(theme.cardBorderColor ? { borderColor: theme.cardBorderColor } : {}),
    overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    ...(theme.cardHoverShadow
      ? {
          "&:hover": {
            boxShadow: theme.cardHoverShadow,
          },
        }
      : {}),
  };
}

function buildButtonSx(theme) {
  return {
    borderRadius: 3,
    px: 4,
    py: 1.5,
    fontSize: "1rem",
    fontWeight: 600,
    background: theme.buttonBg,
    boxShadow: theme.buttonShadow(0.35),
    "&:hover": {
      background: theme.buttonHover,
      boxShadow: theme.buttonHoverShadow(0.4),
      transform: "translateY(-2px)",
    },
    "&:disabled": {
      background: alpha(theme.accent, 0.3),
    },
    transition: "all 0.3s ease",
  };
}

function buildResultCardSx(theme) {
  if (theme.resultCardBg) {
    return {
      background: theme.resultCardBg,
      border: "2px solid",
      borderColor: theme.resultBorderColor,
      borderRadius: 3,
      mt: 2,
    };
  }
  return {
    background: theme.cardBg,
    border: "2px solid",
    borderColor: theme.accentRgba(0.3),
    borderRadius: 3,
    mt: 2,
    boxShadow: `0 2px 8px ${theme.accentRgba(0.1)}`,
  };
}

function buildPremiumResultSx(theme) {
  return {
    fontWeight: 700,
    background: theme.titleGradient,
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    mb: 1,
  };
}

function syncBondTypeFormFields(bondType, setValue) {
  if (bondType === "Blanket") {
    setValue("bondSubCategory", "Blanket");
    setValue("bondAmount", "");
    setValue("dateOfBirth", "");
  } else {
    setValue("bondTerm", "");
  }
}

function buildTabsSx(variant, theme) {
  return {
    "& .MuiTab-root": {
      textTransform: "none",
      fontWeight: 600,
      fontSize: "1rem",
      py: 3,
      minHeight: 72,
      "&.Mui-selected": {
        color: variant === "public" ? "#000000" : theme.accent,
      },
    },
    "& .MuiTabs-indicator": {
      height: 3,
      borderRadius: "3px 3px 0 0",
      background: theme.buttonBg,
    },
  };
}

function createPremiumSubmitHandler(setLoadingCalculation, setPremium) {
  return (data) => {
    const { bondSubCategory: subCat, bondAmount, dateOfBirth, bondTerm } = data;
    setLoadingCalculation(true);
    try {
      const calculated = calculatePremium(bondAmount, dateOfBirth, subCat, bondTerm);
      setPremium(formatCurrency(calculated));
    } catch (error) {
      console.error("Error calculating premium:", error);
      setPremium(null);
    } finally {
      setLoadingCalculation(false);
    }
  };
}

function createPinellasSubmitHandler(homesteadPercentage, setPinellasBond, setPinellasPremium) {
  return (data) => {
    const { homestead, other } = data;
    const homesteadValue = parseCurrencyInput(homestead);
    const otherValue = parseCurrencyInput(other);
    const homesteadPortion = homesteadValue * (homesteadPercentage / 100);
    const gross = homesteadPortion + otherValue;
    const bondAmount = getBondFromGross(gross);
    const calculated = calculatePremium(bondAmount);
    setPinellasBond(formatCurrency(bondAmount));
    setPinellasPremium(formatCurrency(calculated));
  };
}

async function loadPremiumCalculatorStates(setStates, setLoadingDropdowns) {
  setLoadingDropdowns(true);
  try {
    const statesRes = await apiClient(`/states/0/`, "GET");
    setStates(Array.isArray(statesRes) ? statesRes : statesRes || []);
  } catch (error) {
    console.error("Error fetching states:", error);
  } finally {
    setLoadingDropdowns(false);
  }
}

async function loadHomesteadPercentage(setHomesteadPercentage) {
  try {
    const response = await apiClient("/pinellas-homestead/", "GET");
    setHomesteadPercentage(response.homestead_percentage || 0);
  } catch (error) {
    console.error("Error fetching homestead percentage:", error);
    setHomesteadPercentage(0);
  }
}

/**
 * Shared premium calculator UI and logic.
 * @param {{ variant?: 'authenticated'|'public', header?: React.ReactNode }} props
 */
export default function PremiumCalculatorBase({ variant = "authenticated", header = null }) {
  const theme = THEMES[variant] || THEMES.authenticated;
  const inputSx = buildInputSx(theme);

  const { handleSubmit, control, watch, setValue } = useForm();
  const [premium, setPremium] = useState(null);
  const [states, setStates] = useState([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);
  const [loadingCalculation, setLoadingCalculation] = useState(false);
  const [tab, setTab] = useState(0);
  const { handleSubmit: handleSubmitPinellas, control: controlPinellas } = useForm();
  const [pinellasBond, setPinellasBond] = useState(null);
  const [pinellasPremium, setPinellasPremium] = useState(null);
  const [homesteadPercentage, setHomesteadPercentage] = useState(0);

  const bondType = watch("bondType");
  const bondSubCategory = watch("bondSubCategory");

  const fetchStates = () => loadPremiumCalculatorStates(setStates, setLoadingDropdowns);

  useEffect(() => {
    fetchStates();
  }, []);

  useEffect(() => {
    syncBondTypeFormFields(bondType, setValue);
  }, [bondType, setValue]);

  useEffect(() => {
    loadHomesteadPercentage(setHomesteadPercentage);
  }, []);

  const onSubmit = createPremiumSubmitHandler(setLoadingCalculation, setPremium);

  const onSubmitPinellas = createPinellasSubmitHandler(
    homesteadPercentage,
    setPinellasBond,
    setPinellasPremium
  );

  const titleSx = buildTitleSx(variant, theme);
  const cardSx = buildCardSx(theme);
  const buttonSx = buildButtonSx(theme);
  const resultCardSx = buildResultCardSx(theme);
  const premiumResultSx = buildPremiumResultSx(theme);

  return (
    <>
      {header}
      <Container maxWidth="md" sx={{ py: 4, mt: theme.containerMt }}>
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography variant="h3" component="h1" sx={titleSx}>
            Premium Calculator
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
            Calculate your bond premium instantly with our advanced calculator
          </Typography>
        </Box>

        <Card elevation={0} sx={cardSx}>
          <Box sx={{ borderBottom: 1, borderColor: theme.accentRgba(0.12) }}>
            <Tabs
              value={tab}
              onChange={(e, newVal) => setTab(newVal)}
              centered
              sx={buildTabsSx(variant, theme)}
            >
              <Tab
                icon={<CalculateIcon />}
                iconPosition="start"
                label="Premium Calculator"
                sx={{ gap: 1 }}
              />
              <Tab
                icon={<TrendingUpIcon />}
                iconPosition="start"
                label="Pinellas County Calculator"
                sx={{ gap: 1 }}
              />
            </Tabs>
          </Box>

          <CardContent sx={{ p: 4 }}>
            <Fade in={tab === 0} timeout={300}>
              <Box sx={{ display: tab !== 0 ? "none" : "block" }}>
                <Box sx={{ textAlign: "center", mb: 4 }}>
                  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <AttachMoneyIcon sx={{ color: "#000000" }} />
                    <Typography variant="h5" fontWeight={600} sx={{ color: "#000000" }}>
                      Premium Calculator
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary">
                    Enter your bond details to calculate the premium
                  </Typography>
                </Box>

                {loadingDropdowns ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      minHeight: 300,
                    }}
                  >
                    <Box sx={{ textAlign: "center" }}>
                      <CircularProgress size={40} thickness={4} />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        Loading calculator...
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={3}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ mb: 1, fontWeight: 600, color: "text.primary" }}
                        >
                          State *
                        </Typography>
                        <Controller
                          name="state"
                          control={control}
                          defaultValue=""
                          render={({ field }) => (
                            <TextField
                              {...field}
                              select
                              fullWidth
                              variant="outlined"
                              required
                              placeholder="Select your state"
                              sx={inputSx}
                            >
                              {states.map((option) => (
                                <MenuItem
                                  key={option.bond_state_id}
                                  value={option.bond_state_id}
                                >
                                  {option.state_name}
                                </MenuItem>
                              ))}
                            </TextField>
                          )}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ mb: 1, fontWeight: 600, color: "text.primary" }}
                        >
                          Bond Type *
                        </Typography>
                        <Controller
                          name="bondType"
                          control={control}
                          defaultValue=""
                          render={({ field }) => (
                            <TextField
                              {...field}
                              select
                              fullWidth
                              variant="outlined"
                              required
                              placeholder="Select bond type"
                              sx={inputSx}
                            >
                              {categories.map((option) => (
                                <MenuItem key={option.category} value={option.category}>
                                  {option.category}
                                </MenuItem>
                              ))}
                            </TextField>
                          )}
                        />
                      </Grid>

                      {bondType === "Guardianship" && (
                        <Grid size={{ xs: 12 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ mb: 1, fontWeight: 600, color: "text.primary" }}
                          >
                            Bond Sub Category *
                          </Typography>
                          <Controller
                            name="bondSubCategory"
                            control={control}
                            defaultValue=""
                            render={({ field }) => (
                              <TextField
                                {...field}
                                select
                                fullWidth
                                variant="outlined"
                                required
                                placeholder="Select sub category"
                                sx={inputSx}
                              >
                                {categories
                                  .find((cat) => cat.category === "Guardianship")
                                  .sub_categories.map((subCat) => (
                                    <MenuItem key={subCat} value={subCat}>
                                      {subCat}
                                    </MenuItem>
                                  ))}
                              </TextField>
                            )}
                          />
                        </Grid>
                      )}

                      {bondType === "Blanket" && (
                        <Grid size={{ xs: 12 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ mb: 1, fontWeight: 600, color: "text.primary" }}
                          >
                            Bond Term *
                          </Typography>
                          <Controller
                            name="bondTerm"
                            control={control}
                            defaultValue=""
                            render={({ field }) => (
                              <TextField
                                {...field}
                                select
                                fullWidth
                                variant="outlined"
                                required
                                placeholder="Select term"
                                sx={inputSx}
                              >
                                <MenuItem value={1}>1 Year</MenuItem>
                                <MenuItem value={2}>2 Years</MenuItem>
                                <MenuItem value={3}>3 Years</MenuItem>
                              </TextField>
                            )}
                          />
                        </Grid>
                      )}

                      {bondType !== "Blanket" && (
                        <Grid size={{ xs: 12 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ mb: 1, fontWeight: 600, color: "text.primary" }}
                          >
                            Bond Amount *
                          </Typography>
                          <Controller
                            name="bondAmount"
                            control={control}
                            defaultValue=""
                            render={({ field }) => (
                              <TextField
                                {...field}
                                fullWidth
                                variant="outlined"
                                required
                                placeholder="Enter bond amount"
                                slotProps={{
                                  input: {
                                    inputComponent: NumberFormatCustom,
                                    inputProps: { min: 0 },
                                  },
                                }}
                                sx={inputSx}
                              />
                            )}
                          />
                        </Grid>
                      )}

                      {bondSubCategory === "Guardian - Minor" && bondType === "Guardianship" && (
                        <Grid size={{ xs: 12 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ mb: 1, fontWeight: 600, color: "text.primary" }}
                          >
                            Date of Birth *
                          </Typography>
                          <Controller
                            name="dateOfBirth"
                            control={control}
                            defaultValue=""
                            render={({ field }) => (
                              <TextField
                                {...field}
                                type="date"
                                fullWidth
                                variant="outlined"
                                required
                                slotProps={{ inputLabel: { shrink: true } }}
                                sx={inputSx}
                              />
                            )}
                          />
                        </Grid>
                      )}

                      <Grid size={{ xs: 12 }}>
                        <Box sx={{ textAlign: "center", mt: 2 }}>
                          <Button
                            type="submit"
                            variant="contained"
                            size="large"
                            disabled={loadingCalculation}
                            startIcon={
                              loadingCalculation ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                <CalculateIcon />
                              )
                            }
                            sx={buttonSx}
                          >
                            {loadingCalculation ? "Calculating..." : "Calculate Premium"}
                          </Button>
                        </Box>
                      </Grid>

                      {premium && (
                        <Grid size={{ xs: 12 }}>
                          <Fade in={!!premium} timeout={500}>
                            <Card sx={resultCardSx}>
                              <CardContent sx={{ textAlign: "center", py: 3 }}>
                                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                                  Estimated Premium
                                </Typography>
                                <Typography variant="h3" sx={premiumResultSx}>
                                  {premium}
                                </Typography>
                                <Chip
                                  label="Instant Calculation"
                                  sx={
                                    variant === "authenticated"
                                      ? {
                                          borderColor: theme.accentRgba(0.5),
                                          color: theme.accent,
                                        }
                                      : {}
                                  }
                                  color={variant === "public" ? "primary" : undefined}
                                  variant={theme.chipVariant}
                                  size="small"
                                />
                              </CardContent>
                            </Card>
                          </Fade>
                        </Grid>
                      )}
                    </Grid>
                  </form>
                )}
              </Box>
            </Fade>

            <Fade in={tab === 1} timeout={300}>
              <Box sx={{ display: tab !== 1 ? "none" : "block" }}>
                <Box sx={{ textAlign: "center", mb: 4 }}>
                  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <TrendingUpIcon sx={{ color: "#000000" }} />
                    <Typography variant="h5" fontWeight={600} sx={{ color: "#000000" }}>
                      Pinellas County Calculator
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    Specialized calculator for Pinellas County properties
                  </Typography>
                  <Chip
                    label={`Current Homestead Percentage: ${homesteadPercentage}%`}
                    sx={
                      variant === "authenticated"
                        ? { borderColor: theme.accentRgba(0.5), color: theme.accent }
                        : {}
                    }
                    color={variant === "public" ? theme.homesteadChipColor : undefined}
                    variant={theme.chipVariant}
                  />
                </Box>

                <form onSubmit={handleSubmitPinellas(onSubmitPinellas)}>
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: 600, color: "text.primary" }}
                      >
                        Homestead Property Value *
                      </Typography>
                      <Controller
                        name="homestead"
                        control={controlPinellas}
                        defaultValue=""
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            variant="outlined"
                            required
                            placeholder="Enter homestead value"
                            slotProps={{
                              input: {
                                inputComponent: NumberFormatCustom,
                                inputProps: { min: 0 },
                              },
                            }}
                            sx={inputSx}
                          />
                        )}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: 600, color: "text.primary" }}
                      >
                        Other Property Value *
                      </Typography>
                      <Controller
                        name="other"
                        control={controlPinellas}
                        defaultValue=""
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            variant="outlined"
                            required
                            placeholder="Enter other property value"
                            slotProps={{
                              input: {
                                inputComponent: NumberFormatCustom,
                                inputProps: { min: 0 },
                              },
                            }}
                            sx={inputSx}
                          />
                        )}
                      />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ textAlign: "center", mt: 2 }}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          startIcon={<TrendingUpIcon />}
                          sx={buttonSx}
                        >
                          Calculate Bond & Premium
                        </Button>
                      </Box>
                    </Grid>

                    {pinellasBond && pinellasPremium && (
                      <Grid size={{ xs: 12 }}>
                        <Fade in={!!(pinellasBond && pinellasPremium)} timeout={500}>
                          <Card sx={resultCardSx}>
                            <CardContent sx={{ textAlign: "center", py: 3 }}>
                              <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                  <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                                    Estimated Bond
                                  </Typography>
                                  <Typography
                                    variant="h4"
                                    sx={{
                                      fontWeight: 700,
                                      color: theme.resultValueColor || theme.accent,
                                    }}
                                  >
                                    {pinellasBond}
                                  </Typography>
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                  <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                                    Estimated Premium
                                  </Typography>
                                  <Typography
                                    variant="h4"
                                    sx={{
                                      fontWeight: 700,
                                      color: theme.resultPremiumColor || theme.accent,
                                    }}
                                  >
                                    {pinellasPremium}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        </Fade>
                      </Grid>
                    )}
                  </Grid>
                </form>
              </Box>
            </Fade>
          </CardContent>
        </Card>
      </Container>
    </>
  );
}
